import clsx from "clsx";
import {
  Blocks,
  Bot,
  ChevronsLeft,
  ChevronsRight,
  Database,
  FolderOpen,
  GitBranch,
  Grid3x3,
  HelpCircle,
  History,
  LayoutDashboard,
  ListTodo,
  MessageSquare,
  MessagesSquare,
  PaintbrushVertical,
  Palette,
  Settings,
  Shield,
  Sparkles,
  Tag,
  Telescope,
  Users,
  X,
  Zap,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import anthmaiLogo from "@/assets/anthmai-icon.svg";
import anthmaiLogoDark from "@/assets/anthmai-logo-dark.svg";
import anthmaiLogoLight from "@/assets/anthmai-logo-light.svg";
import type { FeatureFlag } from "@/config/featureFlags";
import { featureFlags } from "@/config/featureFlags";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

interface NavItem {
  path: string;
  label: string;
  subtitle?: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  experimental?: boolean;
  /** When set, the item is only shown if this feature flag is enabled. */
  featureFlag?: FeatureFlag;
}

interface NavSection {
  label: string;
  adminOnly?: boolean;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: "Main",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { path: "/chat", label: "Chat", subtitle: "Ask anything", icon: MessageSquare },
      {
        path: "/group-chat",
        label: "Brainstorm",
        subtitle: "Get multiple perspectives",
        icon: MessagesSquare,
        experimental: true,
        featureFlag: "brainstorm",
      },
      {
        path: "/tasks",
        label: "Autopilot",
        subtitle: "Hand off a goal",
        icon: ListTodo,
        experimental: true,
        featureFlag: "autopilot",
      },
      {
        path: "/canvas",
        label: "Canvas",
        subtitle: "Generate documents & visuals",
        icon: PaintbrushVertical,
        experimental: true,
        featureFlag: "canvas",
      },
      {
        path: "/deep-research",
        label: "Deep Research",
        subtitle: "In-depth AI research reports",
        icon: Telescope,
        experimental: true,
      },
    ],
  },
  {
    label: "Agents",
    items: [
      { path: "/agents", label: "My Agents", subtitle: "Build & manage your AI team", icon: Bot },
      {
        path: "/skills",
        label: "My Skills",
        subtitle: "Reusable instruction bundles",
        icon: Zap,
        featureFlag: "skillImport",
      },
      {
        path: "/flows",
        label: "Workflows",
        subtitle: "Build repeatable processes",
        icon: GitBranch,
        featureFlag: "workflows",
      },
      { path: "/builder", label: "AI Builder", subtitle: "Create agents with AI", icon: Sparkles },
    ],
  },
  {
    label: "Resources",
    items: [
      { path: "/teams", label: "Teams", subtitle: "Your team hubs", icon: Users },
      { path: "/projects", label: "Projects", subtitle: "Organize work & resources", icon: FolderOpen },
      { path: "/tags", label: "Tags", subtitle: "Categorize & filter", icon: Tag },
      { path: "/history", label: "History", icon: History },
      { path: "/data-sources", label: "Data Sources", icon: Database },
      { path: "/apps", label: "Custom Apps", icon: Grid3x3, experimental: true, featureFlag: "apps" },
      {
        path: "/apps/view",
        label: "Micro Apps",
        subtitle: "Shareable dashboards",
        icon: LayoutDashboard,
        featureFlag: "microApps",
      },
      {
        path: "/brands",
        label: "Brands",
        subtitle: "Manage brand kits",
        icon: Palette,
        featureFlag: "multiBrand",
      },
    ],
  },
  {
    label: "Admin",
    adminOnly: true,
    items: [
      { path: "/extensions", label: "Extensions", icon: Blocks, adminOnly: true },
      { path: "/admin", label: "Admin", icon: Shield, adminOnly: true },
    ],
  },
  {
    label: "Settings",
    items: [
      { path: "/settings", label: "Settings", icon: Settings },
      { path: "/help", label: "Help", icon: HelpCircle },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// All nav paths, used to resolve the single best (longest-prefix) active item so
// nested routes like `/apps/view` don't also light up their parent (`/apps`).
const ALL_NAV_PATHS = navSections.flatMap((s) => s.items.map((i) => i.path));

/** The most specific nav path that matches the current location, or null. */
function bestMatchPath(pathname: string): string | null {
  let best: string | null = null;
  for (const path of ALL_NAV_PATHS) {
    const matches =
      pathname === path || (path !== "/dashboard" && (pathname === path || pathname.startsWith(`${path}/`)));
    if (matches && (best === null || path.length > best.length)) best = path;
  }
  return best;
}

export function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }: Readonly<SidebarProps>) {
  const { profile } = useAuth();
  const { resolvedTheme } = useTheme();
  const location = useLocation();

  const isPlatformAdmin = profile?.user?.isPlatformAdmin ?? false;

  const filteredSections = navSections
    .filter((section) => !section.adminOnly || isPlatformAdmin)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => (!item.adminOnly || isPlatformAdmin) && (!item.featureFlag || featureFlags[item.featureFlag]),
      ),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-surface-overlay backdrop-blur-xs z-40 lg:hidden"
          role="presentation" // NOSONAR — decorative backdrop element
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 bg-surface-primary border-r border-border-primary flex flex-col transition-all duration-300 lg:static lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
          isCollapsed ? "lg:w-[72px]" : "w-64",
        )}
      >
        {/* Logo */}
        <div className={clsx("border-b border-border-primary", isCollapsed ? "p-4" : "p-6")}>
          {isCollapsed ? (
            /* Collapsed: icon centered, expand button below */
            <div className="flex flex-col items-center gap-2">
              <img src={anthmaiLogo} alt="AnthmAI" className="w-8 h-8 object-contain" />
              <button
                onClick={onToggleCollapse}
                className="p-1 text-content-tertiary hover:text-content-primary rounded-sm transition"
                title="Expand sidebar"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Expanded: full logo */
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <img
                  src={resolvedTheme === "dark" ? anthmaiLogoLight : anthmaiLogoDark}
                  alt="AnthmAI"
                  className="h-9 w-auto object-contain object-left"
                />
              </div>
              {/* Mobile close button */}
              <button
                onClick={onClose}
                className="lg:hidden p-1 text-content-tertiary hover:text-content-primary rounded-sm transition"
              >
                <X className="w-5 h-5" />
              </button>
              {/* Desktop collapse toggle */}
              <button
                onClick={onToggleCollapse}
                className="hidden lg:flex p-1 text-content-tertiary hover:text-content-primary rounded-sm transition"
                title="Collapse sidebar"
              >
                <ChevronsLeft className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {filteredSections.map((section, sectionIndex) => (
            <div key={section.label}>
              {/* Section header — hidden for the first section to keep it clean */}
              {sectionIndex > 0 && (
                <>
                  <div className="border-t border-border-primary mb-3" />
                  {!isCollapsed && (
                    <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-content-quaternary">
                      {section.label}
                    </p>
                  )}
                </>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = bestMatchPath(location.pathname) === item.path;

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      title={isCollapsed ? item.label : undefined}
                      className={clsx(
                        "w-full flex items-center gap-3 rounded-lg transition-all",
                        isCollapsed ? "justify-center px-2 py-3" : "px-4 py-3",
                        isActive
                          ? "nav-active"
                          : "text-content-tertiary hover:bg-interactive-hover hover:text-content-primary",
                      )}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                          <span className="font-medium whitespace-nowrap flex items-center gap-2">
                            {item.label}
                            {/* Beta pills hidden for screenshot */}
                          </span>
                          {item.subtitle && (
                            <span className="block text-[11px] text-content-quaternary leading-tight mt-0.5">
                              {item.subtitle}
                            </span>
                          )}
                        </div>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
